import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Tooltip, Modal, Divider, Flex, Popconfirm, Spin } from "antd";
import { useTableSearch } from "../../../functions/tableSearch";
import EmissionFactorForm from "./EmissionFactorForm";
import { useEffect, useState, useContext } from "react";
import { IndexContext } from "../../../context/IndexContext";
import useFetch from "../../../hooks/useFetch";
import DataTable from "../../../components/common/DataTable";

export default function EmissionFactorView() {
  const { getColumnSearchProps } = useTableSearch();

  const [fetchWrapper] = useFetch();
  const [emissionFactorDetails, setEmissionFactorDetails] = useState([]);

  const [emissionFactorTableLoading, setEmissionFactorTableLoading] =
    useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [formInitialValues, setFormInitialValues] = useState({});

  // load index context
  const { messageApi } = useContext(IndexContext);

  const emissionFactorTableColumns = [
    {
      title: "Scope",
      dataIndex: "ScopeDisplayName",
      key: "ScopeDisplayName",
      width: "6%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.ScopeDisplayName,
        value: item?.ScopeDisplayName,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.ScopeDisplayName.startsWith(value),
    },
    {
      title: "Scope Type",
      dataIndex: "ScopeTypeDisplayName",
      key: "ScopeTypeDisplayName",
      width: "8%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.ScopeTypeDisplayName,
        value: item?.ScopeTypeDisplayName,
      })),
      filterSearch: true,
      onFilter: (value, record) =>
        record.ScopeTypeDisplayName.startsWith(value),
    },
    {
      title: "Category Type",
      dataIndex: "CategoryTypeDisplayName",
      key: "CategoryTypeDisplayName",
      width: "8%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.CategoryTypeDisplayName,
        value: item?.CategoryTypeDisplayName,
      })),
      filterSearch: true,
      onFilter: (value, record) =>
        record.CategoryTypeDisplayName.startsWith(value),
    },
    {
      title: "Emission Source",
      dataIndex: "EmissionSource",
      key: "EmissionSource",
      width: "8%",
      ...getColumnSearchProps("EmissionSource"),
    },
    {
      title: "Dimension",
      dataIndex: "Dimension",
      key: "Dimension",
      width: "8%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.Dimension,
        value: item?.Dimension,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.Dimension.startsWith(value),
    },
    {
      title: "Factor Year",
      dataIndex: "FactorYear",
      key: "FactorYear",
      width: "6%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.FactorYear,
        value: item?.FactorYear,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.FactorYear.startsWith(value),
    },
    {
      title: "Factor Source",
      dataIndex: "FactorSource",
      key: "FactorSource",
      width: "8%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.FactorSource,
        value: item?.FactorSource,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.FactorSource.startsWith(value),
    },
    {
      title: "Factor Type",
      dataIndex: "FactorType",
      key: "FactorType",
      width: "6%",
      ...getColumnSearchProps("FactorType"),
    },
    {
      title: "Factor Value",
      dataIndex: "FactorValue",
      key: "FactorValue",
      width: "6%",
      ...getColumnSearchProps("FactorValue"),
    },
    {
      title: "Factor Unit",
      dataIndex: "FactorUnit",
      key: "FactorUnit",
      width: "6%",
      ...getColumnSearchProps("FactorUnit"),
    },
    {
      title: "Gas Type",
      dataIndex: "GasType",
      key: "GasType",
      width: "6%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.GasType,
        value: item?.GasType,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.GasType.startsWith(value),
    },
    {
      title: "Region",
      dataIndex: "Region",
      key: "Region",
      width: "6%",
      filters: (emissionFactorDetails ?? []).map((item) => ({
        text: item?.Region,
        value: item?.Region,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.Region.startsWith(value),
    },
    {
      title: "Is Biogenic?",
      dataIndex: "IsBiogenic",
      key: "IsBiogenic",
      width: "6%",
      filters: [
        { text: "Yes", value: true },
        { text: "No", value: false },
      ],
      onFilter: (value, record) => record.IsBiogenic === value,
      render: (_, record) => (record.IsBiogenic ? "Yes" : "No"),
    },
    {
      title: "Remark",
      dataIndex: "Remark",
      key: "Remark",
      width: "6%",
      ...getColumnSearchProps("Remark"),
    },
    {
      title: "Action",
      dataIndex: "Action",
      key: "Action",
      width: "8%",
      render: (_, record) => (
        <Flex justify="flex-start" align="center" gap={"5px"} wrap={true}>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: "blue" }} />}
              onClick={() => {
                // getEmissionFactorFormData(record);
                setFormInitialValues(record);
                setIsEditModalOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Delete Emission Factor"
              description={"Are you sure to delete this emission factor?"}
              onConfirm={() => {
                deleteEmissionFactor(record);
              }}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                icon={<DeleteOutlined style={{ color: "red" }} />}
              />
            </Popconfirm>
          </Tooltip>
        </Flex>
      ),
    },
  ];

  /* API to get emission factor details */
  function getEmissionFactorDetails() {
    setEmissionFactorTableLoading(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/getEmissionFactorDetails",
      onSuccess: (response) => {
        setEmissionFactorTableLoading(false);
        setEmissionFactorDetails(response.data);
      },
    });
  }

  function deleteEmissionFactor(row_record) {
    setEmissionFactorTableLoading(true);
    fetchWrapper({
      endpoint_url: "emission_factor_management/deleteEmissionFactor",
      body: {
        emission_factor_id: row_record.ID,
      },
      onSuccess: (response) => {
        setEmissionFactorTableLoading(false);
        if (response.data.status === true) {
          messageApi.open({
            type: "success",
            content: response.message,
          });
          getEmissionFactorDetails();
        } else {
          messageApi.open({
            type: "error",
            content: response.message,
          });
        }
      },
    });
  }

  useEffect(() => {
    getEmissionFactorDetails();
  }, []);

  return (
    <>
      <Flex>
        <Button
          icon={<PlusOutlined />}
          className="mb-5"
          onClick={() => setIsAddModalOpen(true)}
        >
          New Emission Factor
        </Button>
      </Flex>

      {emissionFactorTableLoading === true ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin />
        </Flex>
      ) : (
        <DataTable
          table_column={emissionFactorTableColumns}
          table_data={emissionFactorDetails}
          scroll_width={1500}
        />
      )}

      <Modal
        title="Add Emission Factor"
        open={isAddModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
        width={"700px"}
        destroyOnClose
      >
        <Divider className="mb-4 mt-0"></Divider>
        <EmissionFactorForm
          mode="add"
          setIsEmissionFactorFormModalOpen={setIsAddModalOpen}
          isUserGroupForm={false}
          refreshTable={getEmissionFactorDetails}
        />
      </Modal>

      <Modal
        title="Edit Emission Factor"
        open={isEditModalOpen}
        footer={null}
        closable={false}
        maskClosable={false}
        width={"700px"}
        destroyOnClose
      >
        <Divider className="mb-4 mt-0"></Divider>
        <EmissionFactorForm
          mode="edit"
          setIsEmissionFactorFormModalOpen={setIsEditModalOpen}
          isUserGroupForm={false}
          refreshTable={getEmissionFactorDetails}
          initialValues={formInitialValues}
        />
      </Modal>
    </>
  );
}
